import { ERROR, LOADING, READY, useVisualState } from '../../../hooks/useVisualState'
import React, { useCallback, useEffect, useRef, useState } from 'react'
import { get, post } from '../../../helpers/request'

import Button from '../../../components/Button/Button'
import { Form } from '../../../components/Form/Form'
import ImageUploader from '../../../components/ImageUploader/ImageUploader'
import Modal from '../../../components/Modal/Modal'
import ModalContent from '../../../components/Modal/ModalContent'
import ModalFooter from '../../../components/Modal/ModalFooter'
import ModalHeader from '../../../components/Modal/ModalHeader'
import ProgressBar from '../../../components/ProgressBar/ProgressBar'
import Select from '../../../components/Select/Select'
import SelectItem from '../../../components/Select/SelectItem'
import TextField from '../../../components/TextField/TextField'
import { closeEvent } from '../../../components/Modal/ModalEvents'
import t from '../../../helpers/i18n'
import { uploadFileHelper } from '../../../helpers/upload'
import useObjectState from '../../../hooks/useObjectState'
import { useParams } from 'react-router-dom'

const BANNER_SIZES = {
  home: {
    desktop: {
      width: 2304,
      height: 240
    },
    tablet: {
      width: 1536,
      height: 204
    },
    mobile: {
      width: 960,
      height: 180
    }
  },
  matches: {
    desktop: {
      width: 1456,
      height: 192
    },
    tablet: {
      width: 1056,
      height: 192
    },
    mobile: {
      width: 896,
      height: 200
    }
  },
  leaders: {
    desktop: {
      width: 1456,
      height: 128
    },
    tablet: {
      width: 1392,
      height: 120
    },
    mobile: {
      width: 764,
      height: 130
    }
  },
  fans: {
    desktop: {
      width: 2304,
      height: 240
    },
    tablet: {
      width: 1536,
      height: 160
    },
    mobile: {
      width: 960,
      height: 160
    }
  },
  sidebar: {
    desktop: {
      width: 468,
      height: 280
    },
    tablet: {
      width: 468,
      height: 280
    },
    mobile: {
      width: 468,
      height: 280
    }
  }
}

const BannersFormModal = ({ onCloseRequested }) => {
  const [[bannersState, file], setBanners] = useVisualState()
  const [formValid, setFormValid] = useState(false)
  const [bannerType, setBannerType] = useState()
  const [uploading, setUploading] = useObjectState()
  const editBannersData = useRef()
  const pendingBannersData = useRef()
  const cancel = useRef(false)
  const uploadModal = useRef()

  const { siteId, siteBannerId } = useParams()

  const handleSave = useCallback(
    (target) => {
      const paths = {
        banner_desktop: 'banner',
        banner_tablet: 'banner',
        banner_mobile: 'banner'
      }
      uploadFileHelper(pendingBannersData.current, paths, setUploading)
        .then((data) =>
          Object.keys(data).reduce((final, key) => {
            final[key] = data[key].id
            return final
          }, {})
        )
        .then((data) => {
          if (!cancel.current) {
            const url =
              '/soccer/sites/' +
              siteId +
              '/banners' +
              (siteBannerId !== 'create' ? '/' + siteBannerId : '')
            post(url, { ...editBannersData.current, ...data })
              .then(() => {
                closeEvent(target, true)
              })
              .catch((error) => {
                // TODO: better error handling
                console.log(error)
              })
          }
        })
    },
    [siteId, siteBannerId, setUploading]
  )

  const handleCancelUpload = useCallback(() => {
    cancel.current = true
    closeEvent(uploadModal.current, false)
  }, [])

  useEffect(() => {
    if (siteBannerId && siteBannerId !== 'create') {
      setBanners(LOADING)
      get('/soccer/sites/' + siteId + '/banners/' + siteBannerId)
        .then((response) => {
          setBanners(READY, response)
        })
        .catch((error) => {
          setBanners(ERROR, error)
        })
    } else {
      setBanners(READY, {})
    }
  }, [siteId, siteBannerId, setBanners])

  return (
    <div>
      {bannersState === READY && (
        <Modal onCloseRequested={onCloseRequested}>
          <ModalHeader
            title={
              siteBannerId !== 'create' ? t('modal_headers.Edit_file') : t('modal_headers.Add_file')
            }
          />
          <ModalContent>
            <Form
              data={editBannersData}
              pending={pendingBannersData}
              onValidityChange={setFormValid}
            >
              <TextField
                name='link'
                label={t('labels.Destiny_league')}
                initialValue={file.link}
                maxLength={100}
                minLength={1}
                required
              />
              <Select
                name='type'
                label={t('labels.Ad_location')}
                initialValue={file.type}
                onChange={setBannerType}
              >
                <SelectItem value='home'>{t('select_item.Principal')}</SelectItem>
                <SelectItem value='matches'>{t('select_item.Best_matches')}</SelectItem>
                <SelectItem value='leaders'>{t('select_item.Positions_table')}</SelectItem>
                <SelectItem value='fans'>{t('select_item.FANS_Stats')}</SelectItem>
                <SelectItem value='sidebar'>{t('select_item.Side_menu')}</SelectItem>
              </Select>
              {bannerType && BANNER_SIZES[bannerType].desktop && (
                <ImageUploader
                  name='banner_desktop'
                  label={
                    t('labels.Desktop_image') +
                      BANNER_SIZES[bannerType].desktop.width +
                      'px' + t('labels.for') +
                      BANNER_SIZES[bannerType].desktop.height +
                      'px)'
                  }
                  finalWidth={BANNER_SIZES[bannerType].desktop.width}
                  finalHeight={BANNER_SIZES[bannerType].desktop.height}
                  aspect={
                    BANNER_SIZES[bannerType].desktop.width /
                    BANNER_SIZES[bannerType].desktop.height
                  }
                  src={
                    file.banner_desktop &&
                    process.env.REACT_APP_IMAGE_BASE_URL +
                      '/banner/' +
                      file.banner_desktop
                  }
                />
              )}
              {bannerType && BANNER_SIZES[bannerType].tablet && (
                <ImageUploader
                  name='banner_tablet'
                  label={
                    t('labels.Image_tablets') +
                      BANNER_SIZES[bannerType].tablet.width +
                      'px' + t('labels.for') +
                      BANNER_SIZES[bannerType].tablet.height +
                      'px)'
                  }
                  finalWidth={BANNER_SIZES[bannerType].tablet.width}
                  finalHeight={BANNER_SIZES[bannerType].tablet.height}
                  aspect={
                    BANNER_SIZES[bannerType].tablet.width /
                    BANNER_SIZES[bannerType].tablet.height
                  }
                  src={
                    file.banner_tablet &&
                    process.env.REACT_APP_IMAGE_BASE_URL +
                      '/banner/' +
                      file.banner_tablet
                  }
                />
              )}
              {bannerType && BANNER_SIZES[bannerType].mobile && (
                <ImageUploader
                  name='banner_mobile'
                  label={
                    t('labels.Image_on_mobile_devices') +
                      BANNER_SIZES[bannerType].mobile.width +
                      'px' + t('labels.for') +
                      BANNER_SIZES[bannerType].mobile.height +
                      'px)'
                  }
                  finalWidth={BANNER_SIZES[bannerType].mobile.width}
                  finalHeight={BANNER_SIZES[bannerType].mobile.height}
                  aspect={
                    BANNER_SIZES[bannerType].mobile.width /
                    BANNER_SIZES[bannerType].mobile.height
                  }
                  src={
                    file.banner_mobile &&
                    process.env.REACT_APP_IMAGE_BASE_URL +
                      '/banner/' +
                      file.banner_mobile
                  }
                />
              )}
              <Select
                name='status'
                label={t('labels.File_status')}
                initialValue={file.status}
              >
                <SelectItem value='inactive'>{t('select_item.Inactive')}</SelectItem>
                <SelectItem value='published'>{t('select_item.Published')}</SelectItem>
                <SelectItem value='deleted'>{t('select_item.Deleted_(WARNING)')}</SelectItem>
              </Select>
            </Form>
          </ModalContent>
          <ModalFooter>
            <div>
              <Button
                onClick={(event) => closeEvent(event.currentTarget)}
                label={t('buttons.Cancel')}
              />
            </div>
            <div>
              <Button
                disabled={!formValid}
                onClick={(event) => handleSave(event.currentTarget)}
                label={t('buttons.Save')}
              />
            </div>
          </ModalFooter>
          {uploading && (
            <Modal
              elementRef={uploadModal}
              onCloseRequested={handleCancelUpload}
            >
              <ModalHeader title={t('modal_headers.Uploading_images')} back={false} />
              <ModalContent dialog>
                {Object.keys(uploading).map((key) => (
                  <div key={key}>
                    <div className='text-label color-text'>
                      {pendingBannersData.current[key].value.name}
                    </div>
                    <ProgressBar percent={uploading[key]} />
                  </div>
                ))}
              </ModalContent>
            </Modal>
          )}
        </Modal>
      )}
    </div>
  )
}

export default BannersFormModal
